import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';
import useApi from '../../hooks/useApi';

const PORTAL_USER_KEY = 'portal_user';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;
const StatusText = styled.p`
  margin: 0.7rem 0 0;
  color: ${({ $error }) => ($error ? '#b91c1c' : '#1d4ed8')};
  font-size: 0.9rem;
`;
const ControlRow = styled.div`
  margin-top: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  flex-wrap: wrap;
`;
const FilterTabs = styled.div`display:flex; gap:0.45rem;`;
const FilterTab = styled.button`
  border: 1px solid ${({ $active }) => ($active ? '#2563eb' : '#cbd5e1')};
  background: ${({ $active }) => ($active ? '#eff6ff' : '#fff')};
  color: ${({ $active }) => ($active ? '#1d4ed8' : '#475569')};
  border-radius: 999px;
  padding: 0.28rem 0.72rem;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
`;
const SearchInput = styled.input`
  min-width: 240px;
  border: 1px solid #cbd5e1;
  border-radius: 0.6rem;
  padding: 0.45rem 0.62rem;
  color: #0f172a;
  background: #fff;
`;
const TableWrap = styled.div`
  margin-top: 0.9rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.9rem;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  background: #fff;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 980px;
`;
const Th = styled.th`
  background: #f8fafc;
  color: #64748b;
  text-align: left;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.72rem;
  border-bottom: 1px solid #e2e8f0;
`;
const Td = styled.td`
  padding: 0.72rem;
  font-size: 0.88rem;
  color: #1f2937;
  border-top: 1px solid #f1f5f9;
  white-space: nowrap;
`;
const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.14rem 0.5rem;
  font-size: 0.72rem;
  font-weight: 800;
  color: ${({ $status }) => ($status === 'approved' ? '#065f46' : $status === 'rejected' ? '#991b1b' : '#1e3a8a')};
  background: ${({ $status }) => ($status === 'approved' ? '#d1fae5' : $status === 'rejected' ? '#fee2e2' : '#dbeafe')};
`;
const ApproveButton = styled.button`
  border: none;
  border-radius: 0.6rem;
  background: #16a34a;
  color: #fff;
  padding: 0.45rem 0.8rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    background: #cbd5e1;
    color: #64748b;
    cursor: not-allowed;
    pointer-events: none;
  }
`;
const RejectButton = styled.button`
  border: none;
  border-radius: 0.6rem;
  background: #dc2626;
  color: #fff;
  padding: 0.45rem 0.8rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    background: #cbd5e1;
    color: #64748b;
    cursor: not-allowed;
    pointer-events: none;
  }
`;
const ActionCell = styled(Td)`
  position: sticky;
  right: 0;
  background: #fff;
  min-width: 132px;
  box-shadow: -8px 0 12px rgba(15, 23, 42, 0.04);
  white-space: normal;
`;
const ActionButtons = styled.div`
  display: flex;
  gap: 0.4rem;
  justify-content: flex-start;

  @media (max-width: 1024px) {
    flex-direction: column;
  }

  > button {
    padding: 0.4rem 0.56rem;
    font-size: 0.78rem;
  }
`;
const Empty = styled.div`margin-top:1rem; color:#64748b; text-align:center;`;

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.login_id ? parsed : null;
  } catch {
    return null;
  }
};

const formatPracticeType = (value) => {
  if (value === 'position') return '자리연습';
  if (value === 'sentence') return '단문연습';
  return '낱말연습';
};

const PortalTypingApprovalPage = () => {
  const { request, loading } = useApi();
  const portalUser = useMemo(() => readPortalUser(), []);
  const tableWrapRef = useRef(null);
  const [requests, setRequests] = useState([]);
  const [message, setMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchKeyword, setSearchKeyword] = useState('');

  const fetchRequests = async () => {
    if (!portalUser?.login_id) return;
    try {
      const data = await request({
        endpoint: `${BACKEND_BASE_URL}/api/typing/approvals?login_id=${encodeURIComponent(portalUser.login_id)}&status=all`
      });
      setRequests(data?.requests || []);
    } catch (error) {
      setMessage(error?.responseData?.message || '승인 요청 목록을 불러오지 못했습니다.');
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalUser?.login_id]);

  const filteredRequests = useMemo(() => {
    const keyword = (searchKeyword || '').trim().toLowerCase();
    return requests.filter((item) => {
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      if (!keyword) return true;
      const searchTargets = [
        String(item.id || ''),
        String(item.student_name || ''),
        String(item.request_date || ''),
        String(formatPracticeType(item.practice_type) || ''),
        String(item.status || ''),
      ].join(' ').toLowerCase();
      return searchTargets.includes(keyword);
    });
  }, [requests, searchKeyword, statusFilter]);

  const handleApprove = async (requestId) => {
    if (!portalUser?.login_id) return;
    setMessage('');
    try {
      await request({
        endpoint: `${BACKEND_BASE_URL}/api/typing/approvals/${requestId}/approve`,
        method: 'POST',
        body: { login_id: portalUser.login_id }
      });
      setMessage('승인 처리되었습니다.');
      await fetchRequests();
    } catch (error) {
      setMessage(error?.responseData?.message || '승인 처리에 실패했습니다.');
    }
  };
  const handleReject = async (requestId) => {
    if (!portalUser?.login_id) return;
    setMessage('');
    try {
      await request({
        endpoint: `${BACKEND_BASE_URL}/api/typing/approvals/${requestId}/reject`,
        method: 'POST',
        body: { login_id: portalUser.login_id }
      });
      setMessage('반려 처리되었습니다.');
      await fetchRequests();
    } catch (error) {
      setMessage(error?.responseData?.message || '반려 처리에 실패했습니다.');
    }
  };

  const handleTableWheel = (event) => {
    const container = tableWrapRef.current;
    if (!container) return;
    const hasHorizontalOverflow = container.scrollWidth > container.clientWidth;
    if (!hasHorizontalOverflow) return;
    if (event.shiftKey) return;
    const mostlyVerticalScroll = Math.abs(event.deltaY) >= Math.abs(event.deltaX);
    if (!mostlyVerticalScroll) return;
    event.preventDefault();
    container.scrollLeft += event.deltaY;
  };

  return (
    <Wrapper>
      <SectionHeading
        eyebrow="TYPING APPROVAL"
        title="타자 기록 승인 요청"
        description="학생이 보낸 오늘의 타자 연습을 살펴보고, 승인하면 학생 기록에 반영돼요."
      />
      <StatusText $error={/실패|오류|못|권한/.test(message)}>{message}</StatusText>
      <ControlRow>
        <FilterTabs>
          <FilterTab type="button" $active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>전체</FilterTab>
          <FilterTab type="button" $active={statusFilter === 'pending'} onClick={() => setStatusFilter('pending')}>승인 대기</FilterTab>
          <FilterTab type="button" $active={statusFilter === 'approved'} onClick={() => setStatusFilter('approved')}>승인 완료</FilterTab>
          <FilterTab type="button" $active={statusFilter === 'rejected'} onClick={() => setStatusFilter('rejected')}>반려</FilterTab>
        </FilterTabs>
        <SearchInput
          value={searchKeyword}
          onChange={(event) => setSearchKeyword(event.target.value)}
          placeholder="검색: 학생명/유형/요청일/상태"
        />
      </ControlRow>

      {filteredRequests.length > 0 ? (
        <TableWrap ref={tableWrapRef} onWheel={handleTableWheel}>
          <Table>
            <thead>
              <tr>
                <Th>요청번호</Th>
                <Th>학생</Th>
                <Th>요청일</Th>
                <Th>유형</Th>
                <Th>타수</Th>
                <Th>최고타수</Th>
                <Th>정확도</Th>
                <Th>소요시간</Th>
                <Th>상태</Th>
                <Th style={{ position: 'sticky', right: 0, background: '#f8fafc', zIndex: 1 }}>처리</Th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((item) => (
                <tr key={item.id}>
                  <Td>{item.id}</Td>
                  <Td>{item.student_name}</Td>
                  <Td>{item.request_date || '-'}</Td>
                  <Td>{formatPracticeType(item.practice_type)}</Td>
                  <Td>{item.typing_speed ?? '-'}</Td>
                  <Td>{item.best_typing_speed ?? '-'}</Td>
                  <Td>{item.accuracy ?? 0}%</Td>
                  <Td>{item.duration_seconds ?? 0}초</Td>
                  <Td>
                    <StatusBadge $status={item.status}>{item.status || 'pending'}</StatusBadge>
                  </Td>
                  <ActionCell>
                    <ActionButtons>
                      <ApproveButton type="button" disabled={loading || item.status !== 'pending'} onClick={() => handleApprove(item.id)}>
                        {item.status === 'approved' ? '승인완료' : item.status === 'rejected' ? '반려됨' : '승인하기'}
                      </ApproveButton>
                      <RejectButton type="button" disabled={loading || item.status !== 'pending'} onClick={() => handleReject(item.id)}>
                        {item.status === 'rejected' ? '반려완료' : item.status === 'approved' ? '승인완료' : '반려하기'}
                      </RejectButton>
                    </ActionButtons>
                  </ActionCell>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      ) : (
        <Empty>{loading ? '불러오는 중...' : '조건에 맞는 승인 요청이 없습니다.'}</Empty>
      )}
    </Wrapper>
  );
};

export default PortalTypingApprovalPage;
